import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { setSessionItem } from '../../storeManager'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import CountrySelect from '../../pages/UserAccount/components/CountrySelect'
import getCountries from '../../locale/countries'
import { trackRegistrationMethod, trackErrorMessage } from '../../analytics'
import Box from './Box'
import Terms from './Terms'
import LSButton from '../LSButton'
import InputField from './InputField'
import useLocale from '../../hooks/useLocale'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'

import StepHeading from './StepHeading'
import StepCopy from './StepCopy'
import { userDataShape } from '../AccountFlow/accountFlowMachine'

import useDebounce from 'js/hooks/useDebounce'

const emailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/

function SignUpEmailPassword({ userData, emailExistsError, ...props }) {
  const existingEmail = useRef(emailExistsError ? userData.email : null)
  const [error, setError] = useState(() =>
    emailExistsError ? (
      <FormattedMessage {...messages.errorEmailAlreadyConnected} />
    ) : (
      false
    ),
  )
  const [emailValid, setEmailValid] = useState()

  const locale = useLocale()
  const [countryName, setCountryName] = useState('…')
  const [showCountry, setShowCountry] = useState(false)

  const { firstName, password, email, country } = {
    password: '',
    ...userData,
    country: userData.country || locale.country,
  }

  const debouncedEmail = useDebounce(email, 1250)

  const validateEmail = useCallback(() => emailRegex.test(email), [email])

  useEffect(() => {
    const valid = validateEmail()
    setEmailValid(valid)

    if (debouncedEmail === existingEmail.current) return

    if (valid) {
      return setError(null)
    }

    if (debouncedEmail !== '' && !valid) {
      trackErrorMessage({
        type: 'Malformed email address',
        category: 'Signup',
      })
      setError(<FormattedMessage {...messages.errorEmailMalformed} />)
    }
  }, [debouncedEmail, validateEmail])

  useEffect(() => {
    setSessionItem('registration-method', 'Email')
    typeof hj !== 'undefined' && hj('trigger', 'enter_registration_information')
    trackRegistrationMethod({ method: 'Email' })
  }, [])

  const passwordValid = password === '' ? null : password.length >= 8
  const firstNameValid = firstName !== ''

  useEffect(() => {
    getCountries(locale.language).then(({ getName }) => {
      setCountryName(getName(country, locale.language))
    })
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const disabled =
    emailValid !== true || passwordValid !== true || firstNameValid !== true

  return (
    <div className="o-width-limiter flex w-full flex-col">
      <StepHeading>
        <FormattedMessage {...messages.accountflow_account_heading} />
      </StepHeading>
      <StepCopy>
        <p>
          <FormattedMessage {...messages.accountflow_account_intro} />
        </p>
      </StepCopy>
      <Box onBack={props.onBack}>
        <div className="flex h-full flex-col justify-between">
          <form
            action="/"
            onSubmit={(event) => {
              event.preventDefault()
              props.onComplete()
            }}
          >
            <InputField
              id="firstName"
              name="firstName"
              type={firstName}
              valueText={firstName}
              value={firstName}
              valid={firstNameValid}
              onChange={(e) => props.onChange({ firstName: e.target.value })}
              label={<FormattedMessage {...messages.firstName} />}
              dataUid="first_name_input"
              className="w-full"
            />
            <InputField
              id="email"
              name="email"
              value={email}
              valid={emailValid}
              onChange={(e) => {
                props.onChange({ email: e.target.value })
              }}
              error={error}
              label={<FormattedMessage {...messages.email} />}
              type="email"
              dataUid="email_input"
            />
            <InputField
              id="password"
              name="password"
              value={password}
              valid={passwordValid}
              onChange={(e) => props.onChange({ password: e.target.value })}
              label={
                <>
                  <FormattedMessage {...messages.password} /> (
                  <FormattedMessage {...messages.passwordHint} />)
                </>
              }
              type="password"
              dataUid="password_input"
            ></InputField>

            {/* Country */}
            {showCountry ? (
              <div className="c-signup-flow__input-group h-40">
                <label
                  className="c-signup-flow__label c-signup-flow__label--small"
                  htmlFor="countryInput"
                >
                  <FormattedMessage {...messages.country} />
                </label>
                <CountrySelect
                  data-uid="country_input_selector"
                  id="countryInput"
                  decorations={false}
                  value={country}
                  onChange={(e) => props.onChange({ country: e.target.value })}
                  className="c-signup-flow__select"
                />
              </div>
            ) : (
              <p className="c-signup-flow__small-country h-40">
                <FormattedMessage {...messages.country} />
                {': '}
                {countryName} (
                <button
                  type="button"
                  className="c-signup-flow__small-country-button"
                  onClick={() => setShowCountry(true)}
                  data-uid="country_change_button"
                >
                  <FormattedMessage {...messages.change} />
                </button>
                )
              </p>
            )}
            <LSButton
              data-uid="signup_flow_next_button"
              type="submit"
              disabled={disabled}
              variant="primary"
              onClick={() =>
                pushToDataLayer({
                  event: 'signup_form',
                  section: 'account_information',
                  label: undefined,
                  success: error !== undefined ? '0' : '1', //send 1 if the account information is submitted successfully and the user proceeds to the next step successfully, 0 if any error occurs
                  error_message: error, //if an error occurs when the user clicks submit and proceeds to the next step send error messages, if no error send undefined
                })
              }
              upperCase
              size="large"
              link={false}
            >
              <span>
                <FormattedMessage {...messages.next} />
              </span>
            </LSButton>
          </form>
          <Terms />
        </div>
      </Box>
    </div>
  )
}

SignUpEmailPassword.propTypes = {
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  onBack: PropTypes.func,
  userData: userDataShape,
  emailExistsError: PropTypes.bool,
}

export default SignUpEmailPassword
